'use client';
import React, { useEffect, useId, useState } from 'react';
import Link from 'next/link';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { subscribeToUpdates } from '@/lib/email/emailService';

interface SubscribeFormProps {
  defaultSource: 'footer' | 'subscribe-page';
  tone?: 'light' | 'dark';
}

export default function SubscribeForm({ defaultSource, tone = 'light' }: SubscribeFormProps) {
  const uid = useId();
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [agree, setAgree] = useState(false);
  const [website, setWebsite] = useState('');
  const [source, setSource] = useState<string>(defaultSource);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ name?: string; email?: string; agree?: string }>({});

  useEffect(() => {
    try {
      const param = new URLSearchParams(window.location.search).get('source')?.trim();
      if (param) setSource(param.slice(0, 60));
    } catch {
      // keep default source
    }
  }, []);

  const dark = tone === 'dark';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    const next: { name?: string; email?: string; agree?: string } = {};
    if (!firstName.trim()) next.name = 'Enter your first name';
    if (!email.trim()) next.email = 'Enter your email address';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) next.email = 'Enter a valid email address';
    if (!agree) next.agree = 'Please tick the box to join the mailing list';
    setErrors(next);
    setError(null);
    if (Object.keys(next).length > 0) return;

    if (website.trim() !== '') {
      setDone(true);
      return;
    }

    setSubmitting(true);
    try {
      await subscribeToUpdates({ email: email.trim().toLowerCase(), fullName: firstName.trim(), source });
      setDone(true);
    } catch (err: any) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        role="status"
        className={`flex items-start gap-3 rounded-xl p-4 ${dark ? 'bg-white/10 text-white' : 'bg-[#F8F3E9] text-[#581C32]'}`}
      >
        <CheckCircle2 size={22} className="mt-0.5 flex-shrink-0 text-[#C6A667]" aria-hidden="true" />
        <p className="text-base font-semibold">Thank you, you&apos;re subscribed.</p>
      </div>
    );
  }

  const labelCls = `mb-1.5 block text-sm font-semibold ${dark ? 'text-white' : 'text-[#581C32]'}`;
  const inputCls =
    'block w-full min-h-[48px] rounded-lg border border-[#C6A667] bg-white px-3.5 py-2.5 text-base text-[#2B0D18] placeholder:text-[#2B0D18]/40 focus:outline-none focus:ring-2 focus:ring-[#C6A667]';
  const errCls = `mt-1.5 text-xs font-medium ${dark ? 'text-[#F5C2C2]' : 'text-destructive'}`;

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <div
        aria-hidden="true"
        style={{ position: 'absolute', left: '-10000px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }}
      >
        <label htmlFor={`${uid}-website`}>Website</label>
        <input
          id={`${uid}-website`}
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor={`${uid}-name`} className={labelCls}>First name</label>
          <input
            id={`${uid}-name`}
            type="text"
            autoComplete="given-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className={inputCls}
          />
          {errors.name && <p className={errCls}>{errors.name}</p>}
        </div>
        <div>
          <label htmlFor={`${uid}-email`} className={labelCls}>Email</label>
          <input
            id={`${uid}-email`}
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputCls}
          />
          {errors.email && <p className={errCls}>{errors.email}</p>}
        </div>
      </div>

      <div>
        <label htmlFor={`${uid}-agree`} className={`flex cursor-pointer items-start gap-3 text-sm ${dark ? 'text-white' : 'text-[#2B0D18]'}`}>
          <input
            id={`${uid}-agree`}
            type="checkbox"
            checked={agree}
            onChange={(e) => setAgree(e.target.checked)}
            aria-required="true"
            className="mt-0.5 h-5 w-5 flex-shrink-0 rounded border-input accent-[#C6A667]"
          />
          <span>Send me tips, updates and offers from Care Business School by email.</span>
        </label>
        {errors.agree && <p className={errCls}>{errors.agree}</p>}
        <p className={`mt-2 text-xs ${dark ? 'text-white/70' : 'text-[#2B0D18]/70'}`}>
          Read our{' '}
          <Link href="/privacy-policy" className="font-semibold underline underline-offset-2">
            Privacy Notice
          </Link>
          .
        </p>
      </div>

      {error && (
        <p role="alert" className={`rounded-lg px-3 py-2 text-sm font-medium ${dark ? 'bg-white text-destructive' : 'bg-destructive/10 text-destructive'}`}>
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-[#C6A667] px-6 py-3 text-base font-bold text-[#2B0D18] shadow-md transition-all hover:brightness-105 active:scale-95 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
      >
        {submitting ? (<><Loader2 size={18} className="animate-spin" aria-hidden="true" /> Subscribing…</>) : 'Subscribe'}
      </button>
    </form>
  );
}
